"use client";

import { formatInTimeZone } from "date-fns-tz";
import { useCallback, useEffect, useMemo, useState } from "react";
import { css, cx } from "styled-system/css";
import { cf } from "@/components/brand/sfturnos-styles";
import type { AppointmentStatus } from "@prisma/client";
import { useAuth } from "@/providers/AuthProvider";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";
import { labelAppointmentStatus } from "@/lib/dashboard-labels";
import { WhatsAppChatLink, WHATSAPP_LOGO_SRC } from "@/components/dashboard/whatsapp-chat-link";

type ServiceOpt = { id: string; name: string; duration: number };

export type AppointmentEditRow = {
  id: string;
  startTime: string;
  endTime: string;
  clientName: string;
  clientEmail: string | null;
  clientPhone: string | null;
  notes: string | null;
  status: AppointmentStatus;
  depositStatus: string | null;
  depositAmount: number | null;
  depositPaidAt: string | null;
  whatsappUnavailable?: boolean;
  service: { id: string; name: string; requiresDeposit: boolean } | null;
  court?: { id: string; name: string } | null;
};

const statusOptions: AppointmentStatus[] = ["PENDING", "CONFIRMED", "COMPLETED", "NO_SHOW", "CANCELLED"];

export function AppointmentEditDialog({
  appt,
  timezone,
  open,
  onClose,
  onSaved,
}: {
  appt: AppointmentEditRow | null;
  timezone: string;
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { refresh } = useAuth();
  const [services, setServices] = useState<ServiceOpt[]>([]);
  const [clientName, setClientName] = useState("");
  const [clientEmail, setClientEmail] = useState("");
  const [clientPhone, setClientPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState<AppointmentStatus>("CONFIRMED");
  const [serviceId, setServiceId] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [slots, setSlots] = useState<{ iso: string; label: string }[]>([]);
  const [slotIso, setSlotIso] = useState<string | null>(null);

  const mergedSlots = useMemo(() => {
    if (!appt) return slots;
    const list = [...slots];
    const cur = new Date(appt.startTime).getTime();
    const has = list.some((s) => Math.abs(new Date(s.iso).getTime() - cur) < 2000);
    const sameDay = dateStr === formatInTimeZone(new Date(appt.startTime), timezone, "yyyy-MM-dd");
    if (!has && sameDay) {
      list.unshift({
        iso: new Date(appt.startTime).toISOString(),
        label: `${formatInTimeZone(new Date(appt.startTime), timezone, "HH:mm")} (actual)`,
      });
    }
    return list;
  }, [slots, appt, dateStr, timezone]);
  const [busy, setBusy] = useState(false);
  const [waiving, setWaiving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const loadServices = useCallback(async () => {
    const r = await fetchWithSessionSync("/api/dashboard/services", { credentials: "include" }, refresh);
    if (!r.ok) return;
    const list = (await r.json()) as ServiceOpt[];
    setServices(list);
  }, [refresh]);

  const loadSlots = useCallback(async () => {
    if (!appt || !dateStr || !serviceId) {
      setSlots([]);
      return;
    }
    const qs = new URLSearchParams({
      date: dateStr,
      serviceId,
      excludeAppointmentId: appt.id,
    });
    const r = await fetchWithSessionSync(`/api/dashboard/availability?${qs}`, { credentials: "include" }, refresh);
    if (!r.ok) {
      setSlots([]);
      return;
    }
    const j = (await r.json()) as { slots: { iso: string; label: string }[] };
    setSlots(j.slots);
  }, [appt, dateStr, serviceId, refresh]);

  useEffect(() => {
    if (open) void loadServices();
  }, [open, loadServices]);

  useEffect(() => {
    if (!open) return;
    const h = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);

  useEffect(() => {
    if (!appt || !open) return;
    setClientName(appt.clientName);
    setClientEmail(appt.clientEmail ?? "");
    setClientPhone(appt.clientPhone ?? "");
    setNotes(appt.notes ?? "");
    setStatus(appt.status);
    setServiceId(appt.service?.id ?? "");
    const d = formatInTimeZone(new Date(appt.startTime), timezone, "yyyy-MM-dd");
    setDateStr(d);
    setSlotIso(appt.startTime);
    setMsg(null);
  }, [appt, open, timezone]);

  useEffect(() => {
    if (open && dateStr && serviceId && appt) void loadSlots();
  }, [open, dateStr, serviceId, appt, loadSlots]);

  if (!open || !appt) return null;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    const body: Record<string, unknown> = {
      clientName: clientName.trim(),
      clientEmail: clientEmail.trim(),
      clientPhone: clientPhone.trim() || null,
      notes: notes.trim() || null,
      status,
      serviceId,
    };
    const originalIso = new Date(appt!.startTime).toISOString();
    const slotChanged = slotIso && slotIso !== originalIso;
    const serviceChanged = serviceId !== (appt!.service?.id ?? "");
    if (slotChanged || serviceChanged) {
      if (!slotIso) {
        setMsg("Elegí un horario disponible.");
        setBusy(false);
        return;
      }
      body.startTimeIso = new Date(slotIso).toISOString();
    }
    const r = await fetchWithSessionSync(`/api/dashboard/appointments/${appt!.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    }, refresh);
    setBusy(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "No se pudo guardar");
      return;
    }
    onSaved();
    onClose();
  }

  async function waiveDeposit() {
    if (!appt) return;
    setMsg(null);
    setWaiving(true);
    const r = await fetchWithSessionSync(`/api/dashboard/appointments/${appt.id}/waive-deposit`, {
      method: "POST",
      credentials: "include",
    }, refresh);
    setWaiving(false);
    if (!r.ok) {
      const j = (await r.json().catch(() => ({}))) as { error?: string };
      setMsg(j.error ?? "No se pudo condonar");
      return;
    }
    onSaved();
    onClose();
  }

  return (
    <div
      className={css({
        position: "fixed",
        inset: 0,
        zIndex: 50,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: "4",
        bg: "rgba(0,0,0,0.65)",
      })}
      role="dialog"
      aria-modal
      aria-labelledby="edit-appt-title"
      onClick={onClose}
    >
      <div
        className={cx(cf.card, css({ w: "full", maxW: "28rem", maxH: "90vh", overflow: "auto", p: "6" }))}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 id="edit-appt-title" className={css({ fontSize: "lg", fontWeight: "semibold", color: "#0f172a", mb: "1" })}>
          Editar / reagendar turno
        </h2>
        {appt.clientPhone ? (
          <WhatsAppChatLink phone={appt.clientPhone} variant="compact" className={css({ mb: "4" })}>
            <img
              src={WHATSAPP_LOGO_SRC}
              alt=""
              width={14}
              height={14}
              decoding="async"
              className={css({ display: "block", objectFit: "contain", flexShrink: 0 })}
            />
            Chatear por WhatsApp ({appt.clientPhone})
          </WhatsAppChatLink>
        ) : null}
        <form onSubmit={(ev) => void submit(ev)} className={css({ display: "flex", flexDir: "column", gap: "4" })}>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Servicio</span>
            <select
              className={cf.input}
              value={serviceId}
              onChange={(e) => {
                setServiceId(e.target.value);
                setSlotIso(null);
              }}
            >
              {services.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Día (tu zona: {timezone})</span>
            <input
              type="date"
              className={cf.input}
              value={dateStr}
              onChange={(e) => {
                setDateStr(e.target.value);
                setSlotIso(null);
              }}
            />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Hora disponible</span>
            <select
              className={cf.input}
              value={slotIso ?? ""}
              onChange={(e) => setSlotIso(e.target.value || null)}
            >
              <option value="">— Elegí horario —</option>
              {mergedSlots.map((s) => (
                <option key={s.iso} value={s.iso}>
                  {s.label}
                </option>
              ))}
            </select>
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Cliente</span>
            <input className={cf.input} value={clientName} onChange={(e) => setClientName(e.target.value)} required />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Email</span>
            <input className={cf.input} type="email" value={clientEmail} onChange={(e) => setClientEmail(e.target.value)} required />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>
              Teléfono WhatsApp / SMS <span style={{ color: "#b91c1c" }}>*</span>
            </span>
            <input className={cf.input} type="tel" value={clientPhone} onChange={(e) => setClientPhone(e.target.value)} required />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Notas internas</span>
            <textarea className={cx(cf.input, css({ minH: "16", py: "2" }))} value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
          </label>
          <label className={css({ display: "flex", flexDir: "column", gap: "1" })}>
            <span className={css({ fontSize: "xs", color: "#64748b" })}>Estado</span>
            <select className={cf.input} value={status} onChange={(e) => setStatus(e.target.value as AppointmentStatus)}>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {labelAppointmentStatus(s)}
                </option>
              ))}
            </select>
          </label>
          {(appt.service?.requiresDeposit || appt.depositStatus) ? (
            <div className={css({ rounded: "md", bg: "#f8fafc", p: "3", borderWidth: "1px", borderColor: "#e2e8f0" })}>
              <p className={css({ fontSize: "xs", color: "#64748b", mb: "2" })}>Seña</p>
              <p className={css({ fontSize: "sm", color: "#0f172a" })}>
                Estado:{" "}
                <strong>
                  {appt.depositStatus === "pending"
                    ? "Pendiente de pago"
                    : appt.depositStatus === "paid"
                      ? "Pagada"
                      : appt.depositStatus === "waived"
                        ? "Condonada"
                        : appt.depositStatus === "failed"
                          ? "Fallida / liberada"
                          : "—"}
                </strong>
              </p>
              {appt.depositAmount != null ? (
                <p className={css({ fontSize: "sm", color: "#475569", mt: "1" })}>Monto: ${appt.depositAmount}</p>
              ) : null}
              {appt.depositPaidAt ? (
                <p className={css({ fontSize: "xs", color: "#64748b", mt: "1" })}>
                  Pagada el {new Date(appt.depositPaidAt).toLocaleString("es-AR")}
                </p>
              ) : null}
              {appt.depositStatus === "pending" ? (
                <button
                  type="button"
                  disabled={waiving}
                  onClick={() => void waiveDeposit()}
                  className={css({
                    mt: "3",
                    w: "full",
                    py: "2",
                    rounded: "lg",
                    fontSize: "sm",
                    fontWeight: "semibold",
                    color: "#92400e",
                    borderWidth: "1px",
                    borderColor: "#fcd34d",
                    bg: "#fffbeb",
                    cursor: waiving ? "wait" : "pointer",
                  })}
                >
                  {waiving ? "Procesando…" : "Condonar seña (confirmar sin cobro)"}
                </button>
              ) : null}
            </div>
          ) : null}
          {msg ? <p className={css({ fontSize: "sm", color: "#fca5a5" })}>{msg}</p> : null}
          <div className={css({ display: "flex", gap: "3", mt: "2" })}>
            <button
              type="button"
              onClick={onClose}
              className={css({
                flex: 1,
                py: "2.5",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "medium",
                color: "#475569",
                borderWidth: "1px",
                borderColor: "#e2e8f0",
                bg: "#f8fafc",
                cursor: "pointer",
                _hover: { bg: "#f1f5f9", borderColor: "#cbd5e1" },
              })}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={busy}
              className={css({
                flex: 1,
                py: "2.5",
                rounded: "lg",
                fontSize: "sm",
                fontWeight: "semibold",
                color: "white",
                bg: "linear-gradient(90deg, token(colors.brand.600), token(colors.brand.500))",
                border: "none",
                cursor: busy ? "wait" : "pointer",
                opacity: busy ? 0.8 : 1,
              })}
            >
              Guardar
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
